import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { Fab } from 'transfix-ui/components/Fab'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { MarketplaceProvider } from 'utils/context/MarketplaceContext'
import { useRefresh } from 'utils/hooks/useRefresh'
import { CreateLoadContainer } from 'apps/fleet-planner/components/create-load/CreateLoadContainer'
import { DriverOptInModal } from 'apps/fleet-planner/components/driver-led-status/DriverOptInModal'
import {
  ShareLoadToastMessage,
  PAGE,
} from 'apps/fleet-planner/components/driver-led-status/ShareLoadToastMessage'
import { LoadDetailsModal } from 'apps/fleet-planner/components/load-details/LoadDetailsModal'
import { DeleteLoadModal } from 'apps/fleet-planner/components/modals'
import { TAB, TABS, TAB_EXTERNAL_STATUS_MAP } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { LoadsFilters } from 'apps/fleet-planner/pages/loads/LoadsFilters'
import { LoadsTable } from 'apps/fleet-planner/pages/loads/LoadsTable'
import { NoLoads } from 'apps/fleet-planner/pages/loads/NoLoads'
import { SORT_FIELD, ILoadFilters } from 'apps/fleet-planner/types'
import { SORT_ORDER } from 'apps/fleet-planner/types/base'
import { fetchCarriers } from 'apps/fleet-planner/utils/actions/carriers'
import { fetchCustomers } from 'apps/fleet-planner/utils/actions/customers'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { getRecentFacilities } from 'apps/fleet-planner/utils/actions/facilities'
import { fetchLoadCounts } from 'apps/fleet-planner/utils/actions/loadCounts'
import { resetLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { fetchLoads, setActiveTab } from 'apps/fleet-planner/utils/actions/loads'
import {
  closeDriverOptInModal,
  dismissShareLoadToast,
} from 'apps/fleet-planner/utils/actions/modals'
import {
  selectLoadIds,
  selectSampleLoadId,
  selectLoadsFetching,
  selectLoadstotalCount,
  selectLoadsParams,
  selectLoadsError,
  selectLoadsActiveTab,
  selectLoadCounts,
  modalInfoSelector,
  loadDetailsSelector,
  selectRecentFacilities,
} from 'apps/fleet-planner/utils/selectors'
import { Container, TopNavCTA } from 'components/layout'
import { AddIcon } from 'transfix-icons'
import LoadNotFound from '../../components/atoms/LoadNotFound'
import { LoadsStatusUpdatesContainer } from './LoadsStatusUpdatesContainer'
import { DEFAULT_MAX_AGE } from './const'

export const defaultLoadFilters: Partial<ILoadFilters> = {
  customerId: undefined,
  dispatcherId: undefined,
  driverId: undefined,
  dropoffDate: undefined,
  dropoffFacilityId: undefined,
  dropoffLocality: undefined,
  dropoffSubdivisionCode: undefined,
  pickupDate: undefined,
  pickupFacilityId: undefined,
  pickupLocality: undefined,
  pickupSubdivisionCode: undefined,
  referenceNumber: undefined,
  maxAge: DEFAULT_MAX_AGE,
  status: [],
}

export function Loads() {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const { action, id } = useParams<{ id: string; action: string }>()

  const sampleLoadId = useSelector(selectSampleLoadId)
  const modalLoadId = useMemo(() => {
    switch (id) {
      case 'sample':
        return sampleLoadId
      case 'create-load':
        return undefined
      case 'update-status':
        return undefined
      case 'update-eta':
        return undefined
      default:
        return id
    }
  }, [id, sampleLoadId])

  const showEditModal = id === 'create-load' || action === 'edit'
  const showDeleteModal = action === 'delete'
  const showNotFoundPage = id === 'not-found'

  const dispatch = useDispatch()

  const loadIds = useSelector(selectLoadIds)
  const fetching = useSelector(selectLoadsFetching)
  const totalCount = useSelector(selectLoadstotalCount)
  const params = useSelector(selectLoadsParams)
  const error = useSelector(selectLoadsError)
  const { error: apiError } = useSelector(loadDetailsSelector)
  const activeTab = useSelector(selectLoadsActiveTab)
  const counts = useSelector(selectLoadCounts)
  const {
    error: modalError,
    actionInProgress: saving,
    isDriverOptInModalOpen,
    drivers = [],
    shareLoads,
  } = useSelector(modalInfoSelector)
  const facilities = useSelector(selectRecentFacilities)
  const { message = '', shipmentId = '' } = shareLoads || {}
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  useEffectOnce(() => {
    dispatch(fetchCustomers({ pageSize: 500 }))
    dispatch(fetchDrivers())
    dispatch(fetchDispatchers())
    dispatch(fetchCarriers())
    dispatch(
      fetchLoadCounts({
        ...defaultLoadFilters,
        ...params.filters,
      })
    )
  })

  useEffect(() => {
    dispatch(
      fetchLoads({
        ...params,
        pageNumber: 1,
        pageSize: 20,
        sortField: SORT_FIELD.PICKUP,
        sortOrder: activeTab === TAB.READY_FOR_DISPATCH ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        filters: {
          ...defaultLoadFilters,
          ...params.filters,
          status: TAB_EXTERNAL_STATUS_MAP[activeTab],
        },
      })
    )
    dispatch(
      fetchLoadCounts({
        ...defaultLoadFilters,
        ...params.filters,
      })
    )
  }, [dispatch, activeTab])

  /**
   * In the case that an invalid id or route is given (404), display the Load not found page
   */
  useEffect(() => {
    if (apiError?.includes('Error: API route not found.')) {
      history.push('/loads/not-found')
    }
  }, [apiError])

  const changeTabHandler = useCallback(
    (tab: string) => {
      dispatch(setActiveTab(tab as TAB))
    },
    [dispatch]
  )

  const changePageHandler = useCallback(
    (page: number) => dispatch(fetchLoads({ ...params, pageNumber: page })),
    [dispatch, params]
  )

  // Manually re-initialize a component by
  // passing a unique `key` prop. This is used since
  // <CreateLoadModal /> doesn't mount/unmount when shown!
  // TODO: Consider mounting/un-mounting this component instead
  const { key: createModalKey, resetKey: setCreateModalKey } = useRefresh()

  // We also want a changeable key for <LoadDetailsModal /> but it needs to be unique
  const loadDetailsModalKey = createModalKey + 1
  const marketplaceProviderKey = createModalKey + 2

  useEffect(() => {
    if (typeof id === 'undefined') setCreateModalKey()
  }, [setCreateModalKey, id])

  const closeOptInModalHandler = () => dispatch(closeDriverOptInModal())
  const closeShareLoadToastHandler = () => dispatch(dismissShareLoadToast())

  const addLoadHandler = useCallback(() => {
    trackEvent('Load List', 'OPEN_ADD_LOAD_MODAL_LL')
    history.push('/loads/create-load')
  }, [history])

  // prettier-ignore
  const changeSortingHandler = useCallback(
    (sortField: SORT_FIELD, sortOrder?: SORT_ORDER) => dispatch(
        fetchLoads({
          ...params,
          pageNumber: 1,
          sortField,
          sortOrder:
            sortOrder
            || (sortField === params.sortField && params.sortOrder === SORT_ORDER.DESC
              ? SORT_ORDER.ASC
              : SORT_ORDER.DESC),
        })
      ),
    [params, dispatch]
  )

  const ready = !(fetching || error)

  const handleLoadInfoClose = () => {
    setCreateModalKey()
    dispatch(resetLoadDetails())
    history.push('/loads')
  }

  const isFilteredView = useMemo(() => !isEmpty(omit(params.filters, 'status')), [params.filters])

  const filterChangeHandler = useCallback(
    (filters: Partial<ILoadFilters>) => {
      const newFilters = {
        ...params.filters,
        ...filters,
      }
      dispatch(
        fetchLoads({
          ...params,
          pageNumber: 1,
          pageSize: 20,
          filters: newFilters,
        })
      )
      dispatch(fetchLoadCounts(newFilters))
    },
    [dispatch, params]
  )

  const clearAllFiltersHandler = useCallback(() => filterChangeHandler(defaultLoadFilters), [
    filterChangeHandler,
  ])

  const { isMobile } = useBreakpointContext()

  /**
   * If facilities are empty, fetch recent facilities
   * to populate Pickup and Dropoff location autosuggests.
   * This should only run once
   */
  const hasFetchedFacility = useRef(false)
  useEffect(() => {
    if (!facilities?.length && !hasFetchedFacility.current) {
      dispatch(getRecentFacilities({ pageSize: 1000 }))
      hasFetchedFacility.current = true
    }
  }, [dispatch, facilities?.length])

  if (showNotFoundPage) {
    return <LoadNotFound />
  }

  return (
    <>
      <TopNav title='My Loads'>
        {isMobile ? (
          <Fab aria-label='Add Load' onClick={addLoadHandler} color='primary' size='large'>
            <AddIcon />
          </Fab>
        ) : (
          <TopNavCTA label='Add Load' onClick={addLoadHandler} large />
        )}
      </TopNav>
      <Container hidePadding>
        <LoadsFilters
          onFilterChange={filterChangeHandler}
          currentFilters={params.filters}
          onClearFilter={clearAllFiltersHandler}
          onSortChange={changeSortingHandler}
        />
        <div className='container pt0'>
          <Tabs dataTest='load-list-tabs' value={activeTab} onChange={changeTabHandler}>
            {TABS.map((label, index) => (
              <Tab
                key={label}
                label={label}
                count={counts[index] || 0}
                dataTest={`load-list-tab-${label.toLowerCase().split(' ').join('-')}`}>
                {error && <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />}
                {fetching && <Spinner position='relative' />}
                {ready && (
                  <>
                    {loadIds.length ? (
                      <LoadsTable
                        loadIds={loadIds}
                        totalCount={totalCount}
                        params={params}
                        onPageChange={changePageHandler}
                        onSortingChange={changeSortingHandler}
                        setToast={openToast}
                      />
                    ) : (
                      <NoLoads
                        onAddLoadClick={addLoadHandler}
                        clearFiltersHandler={clearAllFiltersHandler}
                        isFilteredView={isFilteredView}
                      />
                    )}
                  </>
                )}
              </Tab>
            ))}
          </Tabs>
        </div>
        {/** New Create Load overlay */}
        {showEditModal && (
          <CreateLoadContainer
            key={createModalKey}
            defaultValues={{}}
            showModal={showEditModal}
            error={modalError}
            saving={saving}
          />
        )}
        {modalLoadId && (
          <>
            <DriverOptInModal
              id={modalLoadId}
              onSuccess={openToast}
              drivers={drivers}
              isOpen={isDriverOptInModalOpen}
              onClose={closeOptInModalHandler}
            />
            <MarketplaceProvider key={marketplaceProviderKey}>
              {/**
               * Added a key property to <MarketplaceProvider /> to
               * re-mount it and initialize data when a new load is selected
               */}
              <LoadDetailsModal
                key={loadDetailsModalKey}
                id={modalLoadId}
                onClose={handleLoadInfoClose}
                refetchOptions
              />
            </MarketplaceProvider>
            {action === 'delete' && (
              <DeleteLoadModal
                id={modalLoadId}
                showDeleteModal={showDeleteModal}
                saving={saving}
                error={modalError}
              />
            )}
          </>
        )}
        <ShareLoadToastMessage
          page={PAGE.loads}
          shipmentId={shipmentId}
          message={message}
          onClose={closeShareLoadToastHandler}
        />
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
        {(id === 'update-status' || id === 'update-eta') && (
          <MarketplaceProvider>
            <LoadsStatusUpdatesContainer />
          </MarketplaceProvider>
        )}
      </Container>
    </>
  )
}

export default Loads
